import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Switch
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Assignment as AssignmentIcon } from '@mui/icons-material';
import { createTrade } from './tradesSlice';
import EventPicker from './EventPicker';
import { tradePlansApi } from '../../api/tradePlans';

const CreateTradePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({
    assetSymbol: '',
    assetType: 'equity',
    direction: 'long',
    timeframe: '1D',
    entryPrice: '',
    entryAt: new Date().toISOString().slice(0, 16), // Current date/time for convenience
    positionSize: '',
    initialStop: '',
    initialTakeProfit: '',
    reasonText: '',
    planId: null,
    isPaperTrade: false,
  });
  const [selectedEvents, setSelectedEvents] = useState([]);

  useEffect(() => {
    loadPlans();
    
    // Check if planId is in URL params
    const planIdFromUrl = searchParams.get('planId');
    if (planIdFromUrl) {
      setForm(prev => ({ ...prev, planId: planIdFromUrl }));
    }
  }, [searchParams]);

  const loadPlans = async () => {
    try {
      const response = await tradePlansApi.listTradePlans();
      // Handle different response structures
      const extractedPlans = Array.isArray(response) ? response :
        (response && Array.isArray(response.plans)) ? response.plans :
        (response && response.data && Array.isArray(response.data.plans)) ? response.data.plans :
        (response && Array.isArray(response.data)) ? response.data : [];
      setPlans(extractedPlans);
    } catch (err) {
      console.error('Failed to load trade plans:', err);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setForm(prev => ({ 
      ...prev, 
      planId: plan?.planId || null,
      timeframe: plan?.timeframe || prev.timeframe,
      reasonText: plan?.entryCriteria ? `Following ${plan.name}: ${plan.entryCriteria}` : prev.reasonText
    }));
  };

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onTogglePaperTrade = (event) => setForm(prev => ({ ...prev, isPaperTrade: event.target.checked }));
  
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const payload = {
        ...form,
        entryPrice: Number(form.entryPrice),
        positionSize: form.positionSize ? Number(form.positionSize) : undefined,
        initialStop: form.initialStop ? Number(form.initialStop) : undefined,
        initialTakeProfit: form.initialTakeProfit ? Number(form.initialTakeProfit) : undefined,
        entryAt: new Date(form.entryAt).toISOString(), // Convert to ISO string
        linkedEvents: selectedEvents.map(event => `${event.symbol}_${event.eventType}_${event.date}`),
        isPaperTrade: form.isPaperTrade,
      };
      await dispatch(createTrade(payload));
      navigate('/trades');
    } catch (err) {
      setError(err.message || 'Failed to create trade');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" mb={4} gap={2}>
        <IconButton
          onClick={() => navigate('/trades')}
          sx={{ 
            borderRadius: 0,
            border: '1px solid var(--dwlf-ink-10)',
            '&:hover': {
              borderColor: 'var(--dwlf-accent)',
              backgroundColor: 'var(--dwlf-accent-05)',
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography 
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              fontWeight: 600,
              letterSpacing: '-0.02em',
            }}
          >
            Create Trade
          </Typography>
          <Typography 
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.8rem',
              color: 'var(--dwlf-ink-50)',
            }}
          >
            Log a new trade entry
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: { xs: 3, md: 4 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 0 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={onSubmit}>
          <Stack
            spacing={4}
            divider={<Divider flexItem sx={{ borderColor: 'var(--dwlf-ink-06)' }} />}
          >
            <Box>
              <Typography 
                sx={{ 
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontSize: '1rem',
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                Trade Details
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <TextField
                    fullWidth
                    label="Asset Symbol"
                    name="assetSymbol"
                    value={form.assetSymbol}
                    onChange={onChange}
                    required
                    placeholder="e.g., TSLA, BTC/USD"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Asset Type</InputLabel>
                    <Select
                      name="assetType"
                      value={form.assetType}
                      onChange={onChange}
                      label="Asset Type"
                    >
                      <MenuItem value="equity">Equity</MenuItem>
                      <MenuItem value="crypto">Crypto</MenuItem>
                      <MenuItem value="fx">FX</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Direction</InputLabel>
                    <Select
                      name="direction"
                      value={form.direction}
                      onChange={onChange}
                      label="Direction"
                    >
                      <MenuItem value="long">Long</MenuItem>
                      <MenuItem value="short">Short</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <TextField
                    fullWidth
                    label="Timeframe"
                    name="timeframe"
                    value={form.timeframe}
                    onChange={onChange}
                    placeholder="e.g., 1D, 4H, 1H"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isPaperTrade}
                        onChange={onTogglePaperTrade}
                        name="isPaperTrade"
                        color="primary"
                      />
                    }
                    label="Mark as Paper Trade"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                  <FormControl fullWidth sx={{ minWidth: 220 }}>
                    <InputLabel>Trade Plan (Optional)</InputLabel>
                    <Select
                      name="planId"
                      value={form.planId || ''}
                      onChange={(e) => {
                        const planId = e.target.value;
                        if (planId === '') {
                          handlePlanSelect(null);
                        } else {
                          const plan = plans.find(p => p.planId === planId);
                          handlePlanSelect(plan);
                        }
                      }}
                      label="Trade Plan (Optional)"
                    >
                      <MenuItem value="">
                        <em>No plan selected</em>
                      </MenuItem>
                      {plans.map((plan) => (
                        <MenuItem key={plan.planId} value={plan.planId}>
                          {plan.name} ({plan.category})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {selectedPlan && (
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 3,
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    backgroundColor: 'var(--dwlf-bg)',
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1.5}>
                    <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Using Plan: {selectedPlan.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {selectedPlan.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Entry:</strong> {selectedPlan.entryCriteria}
                  </Typography>
                </Paper>
              )}
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Entry & Position Sizing
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <TextField
                    fullWidth
                    label="Entry Price"
                    name="entryPrice"
                    type="number"
                    step="0.0001"
                    value={form.entryPrice}
                    onChange={onChange}
                    required
                    placeholder="0.00"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <TextField
                    fullWidth
                    label="Entry Date & Time"
                    name="entryAt"
                    type="datetime-local"
                    value={form.entryAt}
                    onChange={onChange}
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <TextField
                    fullWidth
                    label="Position Size"
                    name="positionSize"
                    type="number"
                    step="1"
                    value={form.positionSize}
                    onChange={onChange}
                    placeholder="e.g., 100"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <TextField
                    fullWidth
                    label="Initial Stop Loss"
                    name="initialStop"
                    type="number"
                    step="0.0001"
                    value={form.initialStop}
                    onChange={onChange}
                    placeholder="0.00"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <TextField
                    fullWidth
                    label="Initial Take Profit"
                    name="initialTakeProfit"
                    type="number"
                    step="0.0001"
                    value={form.initialTakeProfit}
                    onChange={onChange}
                    placeholder="0.00"
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Trade Notes
              </Typography>
              <TextField
                fullWidth
                label="Trade Reason"
                name="reasonText"
                value={form.reasonText}
                onChange={onChange}
                multiline
                rows={4}
                placeholder="Why did you take this trade? What was your setup?"
              />
            </Box>

            <EventPicker
              selectedEvents={selectedEvents}
              onEventsChange={setSelectedEvents}
              symbol={form.assetSymbol}
            />

            <Box display="flex" justifyContent="flex-end" gap={2} flexWrap="wrap">
              <Button
                variant="outlined"
                onClick={() => navigate('/trades')}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                {loading ? 'Creating...' : 'Create Trade'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};

export default CreateTradePage;
